import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useCreateEscalation,
  useEscalationContext,
} from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ObjectId } from "bson";
import { useContext, useEffect, useRef, useState } from "react";
import * as z from "zod/v4";
import { useCodeSearchContext } from "./code-search-context";
import { useCodeSearchFeatureFlag } from "./code-search-feature-flag-context";

interface UseInChatEscalationParams {
  messages: Message[];
  input: unknown;
  onSuccess: () => void;
}

const CODE_SEARCH_STORAGE_KEY = "code-search-pending";
const MAX_RECONNECTS = 5;
const CODE_SEARCH_STORAGE_TTL_MS = 20 * 60 * 1000;

const CodeSearchStorageSchema = z.object({
  noticeTopic: z.string().min(1),
  threadId: z.string().min(1),
  savedAt: z.number(),
});

const INVESTIGATION_FAILED_MESSAGE =
  "Investigation could not complete. A specialist will follow up.";

/** Categories that skip code search and go straight to Intercom */
const DIRECT_ESCALATION_CATEGORIES = new Set(["churn", "coverage"]);

export function useInChatEscalation({
  messages,
  input,
  onSuccess,
}: UseInChatEscalationParams) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const escalationContext = useEscalationContext();
  const codeSearchContext = useCodeSearchContext();
  const codeSearchEnabled = useCodeSearchFeatureFlag();
  const { addMessage, updateMessage } = useContext(AiChatContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEscalated, setIsEscalated] = useState(false);
  const [isInvestigationDone, setIsInvestigationDone] = useState(false);
  // Snapshot: "user kicked off a code search investigation in this session".
  // Stays true after `codeSearchAttempted` flips so the InvestigatingCard
  // can transition to the "done" state instead of unmounting.
  const [investigationStarted, setInvestigationStarted] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  function buildChatHistory() {
    return messages
      .filter((msg) => msg.role !== "tool")
      .map((msg) => {
        let content: string;
        if (typeof msg.content === "string") {
          content = msg.content;
        } else if (Array.isArray(msg.content)) {
          content = msg.content
            .filter(
              (part): part is { type: "text"; text: string } =>
                part.type === "text",
            )
            .map((part) => part.text)
            .join("\n");
        } else {
          content = "";
        }
        return { id: msg.id ?? undefined, role: msg.role, content };
      }) as {
      id?: string;
      role: "system" | "user" | "assistant";
      content: string;
    }[];
  }

  const createEscalation = useCreateEscalation({
    merchantRpcClient,
    buildChatHistory,
  });

  function finalizeInvestigation({
    errorMessage,
  }: { errorMessage?: string } = {}) {
    if (errorMessage) {
      setError(errorMessage);
    }
    setIsInvestigationDone(true);
    codeSearchContext.update({
      codeSearchAttempted: true,
      pendingCodeSearch: null,
    });
    try {
      sessionStorage.removeItem(CODE_SEARCH_STORAGE_KEY);
    } catch (_) {
      // ignore
    }
  }

  // Stream the agent's response with typing animation
  async function streamAgentResponse(
    threadId: string,
    signal: AbortSignal,
  ): Promise<void> {
    const streamingId = new ObjectId().toString();
    let streamingContent = "";
    let hasStartedStreaming = false;

    const stream = merchantRpcClient.streamMerchantSupportAgent(
      { messages: [], currentThreadId: threadId, triageThreadId: threadId },
      { signal },
    );

    for await (const event of stream) {
      if (signal.aborted) {
        break;
      }

      switch (event.type) {
        case "text-delta":
          if (!hasStartedStreaming) {
            hasStartedStreaming = true;
            addMessage({ id: streamingId, role: "assistant", content: "" });
          }
          streamingContent += event.textDelta;
          updateMessage(streamingId, {
            id: streamingId,
            role: "assistant",
            content: streamingContent,
          });
          break;

        case "complete":
          if (event.thread?.newMessages) {
            const finalMsg = event.thread.newMessages.find(
              (m: Message) =>
                m.role === "assistant" &&
                typeof m.content === "string" &&
                m.content.length > 0,
            );
            if (finalMsg) {
              updateMessage(streamingId, finalMsg);
            }
          }
          finalizeInvestigation();
          break;

        case "error":
          finalizeInvestigation({ errorMessage: INVESTIGATION_FAILED_MESSAGE });
          return;
      }
    }
  }

  // Subscribe to Notice with reconnect logic.
  // Wrapped in useHandler for a stable ref that always calls the latest closure.
  const startSubscription = useHandler(
    (noticeTopic: string, threadId: string): void => {
      const abort = new AbortController();
      abortRef.current?.abort();
      abortRef.current = abort;

      void (async () => {
        let reconnects = 0;
        let investigationDone = false;
        let sawErrorEvent = false;

        while (!abort.signal.aborted && reconnects <= MAX_RECONNECTS) {
          try {
            const stream = merchantRpcClient.watchCodeSearchResult(
              { noticeTopic, triageThreadId: threadId },
              { signal: abort.signal },
            );

            for await (const event of stream) {
              if (abort.signal.aborted) {
                return;
              }
              if (event.type === "result-ready") {
                await streamAgentResponse(threadId, abort.signal);

                if (
                  event.outcome === "needs-human" ||
                  event.outcome === "timed-out" ||
                  event.outcome === "bug" ||
                  event.outcome === "feature-request"
                ) {
                  try {
                    await createEscalation({
                      triageThreadId: threadId,
                      escalationReason:
                        "Code search investigation determined this needs human assistance",
                      issueCategory: "general",
                    });
                  } catch (escalationErr) {
                    console.error(
                      "Failed to create Intercom escalation after code search",
                      escalationErr,
                    );
                  }
                }
                investigationDone = true;
                return;
              }
              if (event.type === "error") {
                if (!sawErrorEvent) {
                  sawErrorEvent = true;
                  setError(INVESTIGATION_FAILED_MESSAGE);
                }
                break;
              }
            }

            reconnects++;
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              return;
            }
            reconnects++;
          }

          if (!abort.signal.aborted) {
            await new Promise((r) =>
              setTimeout(r, Math.min(1000 * 2 ** reconnects, 16_000)),
            );
          }
        }

        if (!abort.signal.aborted && !investigationDone) {
          finalizeInvestigation({ errorMessage: INVESTIGATION_FAILED_MESSAGE });
        }
      })();
    },
  );

  // On mount: check if there's a pending code search from a previous session
  useEffect(() => {
    if (!codeSearchEnabled) {
      abortRef.current?.abort();
      return undefined;
    }

    try {
      const stored = sessionStorage.getItem(CODE_SEARCH_STORAGE_KEY);
      if (stored) {
        let rawParsed: unknown;
        try {
          rawParsed = JSON.parse(stored);
        } catch (_) {
          sessionStorage.removeItem(CODE_SEARCH_STORAGE_KEY);
          rawParsed = undefined;
        }

        const parsed = CodeSearchStorageSchema.safeParse(rawParsed);
        if (!parsed.success) {
          try {
            sessionStorage.removeItem(CODE_SEARCH_STORAGE_KEY);
          } catch (_) {
            // ignore
          }
        } else if (
          Date.now() - parsed.data.savedAt >
          CODE_SEARCH_STORAGE_TTL_MS
        ) {
          try {
            sessionStorage.removeItem(CODE_SEARCH_STORAGE_KEY);
          } catch (_) {
            // ignore
          }
        } else {
          setIsEscalated(true);
          setInvestigationStarted(true);
          startSubscription(parsed.data.noticeTopic, parsed.data.threadId);
        }
      }
    } catch (_) {
      // ignore
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [codeSearchEnabled, startSubscription]);

  const escalate = useHandler(async () => {
    if (!escalationContext.triageThreadId) {
      setError("Unable to escalate: conversation thread not found");
      return;
    }

    // Prevent double-clicks
    if (isEscalated || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const toolInput = input as
        | {
            reason?: string;
            issueCategory?: string;
            customerContext?: string | null;
          }
        | undefined;

      const threadId = escalationContext.triageThreadId;

      // Skip code search: flag disabled, already attempted, or category requires direct escalation
      const skipCodeSearch =
        !codeSearchEnabled ||
        codeSearchContext.state.codeSearchAttempted ||
        DIRECT_ESCALATION_CATEGORIES.has(toolInput?.issueCategory ?? "");
      if (skipCodeSearch) {
        await createEscalation({
          triageThreadId: threadId,
          escalationReason: toolInput?.reason,
          issueCategory: toolInput?.issueCategory,
        });

        setIsLoading(false);
        setIsEscalated(true);
        onSuccess();
        return;
      }

      const { noticeTopic } = await merchantRpcClient.startCodeSearch({
        triageThreadId: threadId,
        escalationReason: toolInput?.reason,
        issueCategory: toolInput?.issueCategory,
        customerContext: toolInput?.customerContext,
      });

      // Persist for recovery (sessionStorage for page refresh, context for panel close)
      codeSearchContext.update({
        pendingCodeSearch: { noticeTopic, threadId },
      });
      try {
        sessionStorage.setItem(
          CODE_SEARCH_STORAGE_KEY,
          JSON.stringify({ noticeTopic, threadId, savedAt: Date.now() }),
        );
      } catch (_) {
        // sessionStorage may be unavailable
      }

      setIsLoading(false);
      setIsEscalated(true);
      setInvestigationStarted(true);
      onSuccess();

      startSubscription(noticeTopic, threadId);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to connect to support";
      setError(errorMessage);
      setIsLoading(false);
    }
  });

  const parsedInput = input as { issueCategory?: string } | undefined;
  const isDirectCategory = DIRECT_ESCALATION_CATEGORIES.has(
    parsedInput?.issueCategory ?? "",
  );

  // Fallback path used when code search fails after all reconnects (or when
  // the stream yields an error event). Mirrors the `skipCodeSearch` branch
  // of `escalate()` — creates an Intercom escalation directly and updates the
  // escalation context. Extracted as its own method so the InvestigatingCard
  // error variant can invoke it without going through `escalate()`, which
  // would re-start code search.
  const escalateToHumanDirectly = useHandler(async () => {
    if (!escalationContext.triageThreadId) {
      setError("Unable to escalate: conversation thread not found");
      return;
    }
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const toolInput = input as
        | {
            reason?: string;
            issueCategory?: string;
            customerContext?: string | null;
          }
        | undefined;

      const threadId = escalationContext.triageThreadId;
      await createEscalation({
        triageThreadId: threadId,
        escalationReason: toolInput?.reason,
        issueCategory: toolInput?.issueCategory,
      });

      setIsLoading(false);
      setIsEscalated(true);
      onSuccess();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to connect to support";
      setError(errorMessage);
      setIsLoading(false);
    }
  });

  return {
    escalate,
    escalateToHumanDirectly,
    isLoading,
    error,
    isEscalated,
    isInvestigationDone,
    investigationStarted,
    shouldSkipCodeSearch:
      !codeSearchEnabled ||
      codeSearchContext.state.codeSearchAttempted ||
      isDirectCategory,
  };
}
